import Script from 'next/script';
import { useCallback, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          action?: string;
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

interface Props {
  onVerify: (token: string | null) => void;
  /** Must match the API `expectedAction` (e.g. signup). */
  action?: string;
}

/**
 * Renders the Cloudflare Turnstile challenge. When no site key is configured
 * the widget is skipped entirely — the API also skips verification in that case.
 */
export default function TurnstileWidget({ onVerify, action }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onVerifyRef = useRef(onVerify);
  const actionRef = useRef(action);
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);

  const handleScriptReady = useCallback(() => setScriptReady(true), []);

  useEffect(() => {
    if (!SITE_KEY || !scriptReady || !containerRef.current) {
      return;
    }

    const turnstile = window.turnstile;
    if (!turnstile) {
      return;
    }

    const actionChanged = actionRef.current !== action;
    actionRef.current = action;

    // Tear down any previous instance so option changes re-bind correctly.
    // Only clear the parent token when `action` actually changes — otherwise a
    // successful challenge would be wiped by unrelated effect re-runs.
    if (widgetIdRef.current) {
      turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
      if (actionChanged) {
        onVerifyRef.current(null);
      }
    }

    widgetIdRef.current = turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      theme: 'light',
      ...(action ? { action } : {}),
      callback: (token) => onVerifyRef.current(token),
      'expired-callback': () => onVerifyRef.current(null),
      'error-callback': () => onVerifyRef.current(null),
    });

    return () => {
      if (widgetIdRef.current) {
        window.turnstile?.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [scriptReady, action]);

  if (!SITE_KEY) {
    return null;
  }

  return (
    <div className="mt-6">
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="lazyOnload"
        onReady={handleScriptReady}
      />
      <div ref={containerRef} />
    </div>
  );
}
